/**
 * card-product-image-slider.js
 * Lightweight image slider for product cards.
 * Supports: arrow buttons, dot indicators, touch/pointer drag.
 */
(function () {
  'use strict';

  if (window.cardProductImageSliderReady) return;
  window.cardProductImageSliderReady = true;

  /** Initialise a single slider element */
  function initSlider(slider) {
    const track = slider.querySelector('.card-img-slider__track');
    const slides = slider.querySelectorAll('.card-img-slider__slide');
    const dots = slider.querySelectorAll('.card-img-slider__dot');
    const btnPrev = slider.querySelector('.card-img-slider__btn--prev');
    const btnNext = slider.querySelector('.card-img-slider__btn--next');
    const card = slider.closest('.card');
    const cardInner = slider.closest('.card__inner');
    const cardLink = card && card.querySelector('.card__heading a[href]');

    if (!track || slides.length < 2) return;

    let current = 0;
    const total = slides.length;

    // ── Helpers ──────────────────────────────────────────
    function goTo(index) {
      // Clamp
      index = Math.max(0, Math.min(total - 1, index));
      current = index;

      track.style.transform = `translateX(-${current * 100}%)`;
      slider.dataset.current = current;

      // Update dots
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === current);
      });

      // Update slides
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === current);
      });

      // Update prev button visibility
      if (btnPrev) btnPrev.disabled = current === 0;
      if (btnNext) btnNext.disabled = current === total - 1;
    }

    // Initialise state
    goTo(0);

    // ── Arrow buttons ─────────────────────────────────────
    if (btnPrev) {
      btnPrev.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goTo(current - 1);
      });
    }

    if (btnNext) {
      btnNext.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goTo(current + 1);
      });
    }

    // ── Dot clicks ────────────────────────────────────────
    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goTo(parseInt(dot.dataset.index, 10));
      });
    });

    slider.addEventListener('card:go-to-slide', function (event) {
      const index = Number(event.detail && event.detail.index);

      if (!Number.isNaN(index)) {
        goTo(index);
      }
    });

    // ── Touch / Pointer drag (on the track only, never on buttons) ───
    var pointerStartX = null;
    var pointerStartY = null;
    var activePointerId = null;
    var dragging = false;
    var dragDeltaX = 0;
    var SWIPE_THRESHOLD = 40; // px

    function onTrackPointerDown(e) {
      // Ignore right-clicks and any click that originated on a button/dot
      if (e.button !== 0) return;
      if (e.target.closest('.card-img-slider__btn, .card-img-slider__dot')) return;

      pointerStartX = e.clientX;
      pointerStartY = e.clientY;
      activePointerId = e.pointerId;
      dragging = false;
      dragDeltaX = 0;

      // Capture on the TRACK so arrow buttons keep their own pointer events
      track.setPointerCapture(e.pointerId);
    }

    function onTrackPointerMove(e) {
      if (activePointerId === null || e.pointerId !== activePointerId) return;
      var dx = e.clientX - pointerStartX;
      var dy = e.clientY - pointerStartY;

      // Only start drag when horizontal movement clearly dominates
      if (!dragging && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 5) {
        dragging = true;
        slider.classList.add('is-dragging');
      }

      if (dragging) {
        dragDeltaX = dx;
        var baseOffset = current * 100;
        track.style.transform = `translateX(calc(-${baseOffset}% + ${dx}px))`;
      }
    }

    function onTrackPointerUp(e) {
      if (activePointerId === null || e.pointerId !== activePointerId) return;
      slider.classList.remove('is-dragging');

      if (dragging) {
        if (dragDeltaX < -SWIPE_THRESHOLD) {
          goTo(current + 1);
        } else if (dragDeltaX > SWIPE_THRESHOLD) {
          goTo(current - 1);
        } else {
          goTo(current); // snap back
        }
      }

      pointerStartX = null;
      pointerStartY = null;
      activePointerId = null;
      dragging = false;
      dragDeltaX = 0;
    }

    track.addEventListener('pointerdown', onTrackPointerDown);
    track.addEventListener('pointermove', onTrackPointerMove);
    track.addEventListener('pointerup', onTrackPointerUp);
    track.addEventListener('pointercancel', onTrackPointerUp);

    // Prevent a drag-end from triggering the card link
    track.addEventListener('click', function (e) {
      if (Math.abs(dragDeltaX) > 5) e.preventDefault();
    });

    if (card) {
      card.classList.add('card--has-image-slider');
    }

    if (cardInner && cardLink && !cardInner.dataset.fullCardLinkReady) {
      cardInner.dataset.fullCardLinkReady = 'true';
      cardInner.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.button !== 0) return;
        if (e.target.closest('a, button, input, select, textarea, summary, details, label, [role="button"]')) return;

        if (e.metaKey || e.ctrlKey) {
          window.open(cardLink.href, '_blank', 'noopener');
          return;
        }

        if (e.shiftKey || e.altKey) return;

        window.location.href = cardLink.href;
      });
    }
  }

  // ── Init all sliders on the page ──────────────────────
  function initAll() {
    document.querySelectorAll('.card-img-slider').forEach(function (el) {
      if (!el.dataset.sliderReady) {
        el.dataset.sliderReady = 'true';
        initSlider(el);
      }
    });
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-run when Shopify section content changes (Theme Editor)
  document.addEventListener('shopify:section:load', initAll);

  // Support dynamically injected cards (infinite scroll, quick-add, etc.)
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          // Check the added node itself
          if (node.classList && node.classList.contains('card-img-slider') && !node.dataset.sliderReady) {
            node.dataset.sliderReady = 'true';
            initSlider(node);
          }
          // Check children of the added node
          node.querySelectorAll && node.querySelectorAll('.card-img-slider').forEach(function (el) {
            if (!el.dataset.sliderReady) {
              el.dataset.sliderReady = 'true';
              initSlider(el);
            }
          });
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
